import { TrendingUp } from '@mui/icons-material'
import { PriceData } from '../../types/charts'
import { DashboardViewProps, PriceGraphWidgetConfig, Widget, WidgetData } from './Widget'
import { AppContextProps } from '../../hooks/useApp'
import { now } from '../../utils/time'
import { DateTime } from 'luxon'
import { TimeString } from '../../types/models'
import { Link } from 'react-router-dom'
import { Box } from '@mui/material'
import { PriceChart } from '../../components/charts/PriceChart'
import { VAT } from '../../utils/vat'
import { SystemControl } from '../SystemControl'
import { RadioSelector } from '../../components/atoms/RadioSelector'

export class PriceGraphWidget extends Widget<{ prices: PriceData[] }, PriceGraphWidgetConfig> {

  dayOffset: 0 | 1 | 2

  constructor(data: WidgetData, control: SystemControl, app: AppContextProps) {
    super(data, control, app)
    this.dayOffset = 0
  }

  get Icon() {
    return TrendingUp
  }

  get width(): number {
    return 4
  }

  get height(): number {
    return 8
  }

  dataUrls(): Record<string, string> {
    const [from] = this.dateRange()

    const currentTime = now()
    // Fetch 3 full days + 56h from now to ensure forecasts fill day-after-tomorrow
    const pricesTo: TimeString = DateTime.local(currentTime.year, currentTime.month, currentTime.day, 23, 0, 0).plus({ hours: 1 + 72 }).toUTC().toISO() as TimeString
    const country = this.app.user?.country?.iso_3166 || 'FI'

    return {
      prices: `/data/prices?from=${from}&to=${pricesTo}&area=${country}`
    }
  }

  dashboardTitle(firstOfKind: boolean): string | undefined {
    return this.customName('Market Prices') || this.app.t('Market Prices')
  }

  draw(props: DashboardViewProps): React.ReactElement {
    const country = this.app.user?.country?.iso_3166 || 'FI'
    const vat = VAT.for(country)
    const t = this.app.t

    const [from, to] = this.dateRange()

    // Detect step size from data: 15-min (96/day) or hourly (24/day)
    const allPrices = this.data?.prices || []
    let perDay = 24
    if (allPrices.length > 2 && allPrices[1]) {
      const stepMs = DateTime.fromISO(allPrices[1].from).toMillis() - DateTime.fromISO(allPrices[0].from).toMillis()
      if (stepMs > 0) {
        perDay = Math.max(1, Math.round(86400000 / stepMs))
      }
    }

    const haveTomorrow = allPrices.filter(p => p.from >= to && p.price !== null).length > 1
    const haveDayAfter = allPrices.length > perDay * 2

    const currentTime = now()
    const pricesTo: TimeString = DateTime.local(currentTime.year, currentTime.month, currentTime.day, 23, 0, 0).plus({ hours: 1 + 48 }).toUTC().toISO() as TimeString

    let prices: PriceData[] = allPrices.slice(this.dayOffset * perDay, (this.dayOffset + 1) * perDay)
    const limitToRange = (priceData: PriceData) => priceData.from >= from && priceData.from < pricesTo
    const priceToCents = (priceData: PriceData) => ({ ...priceData, price: priceData.price === null || priceData.price === undefined ? undefined : priceData.price / 10 })
    prices = prices.filter(limitToRange).map(priceToCents)

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <RadioSelector
            variant="primary"
            value={String(this.dayOffset)}
            onClick={(value) => {
              this.dayOffset = Number(value) as 0 | 1 | 2
              props.refresh && props.refresh()
            }}
            values={{
              0: t('Today'),
              1: t('Tomorrow'),
              ...(haveDayAfter ? { 2: t('Day After Tomorrow') } : {}),
            }}
            isDisabled={(value) => (value === '1' && !haveTomorrow) || (value === '2' && !haveDayAfter)}
          />
        </Box>
        <Link to="/history">
          <PriceChart
            id={`price${this.id || ''}`}
            prices={prices}
            currency={this.app.user?.currency || 'EUR'}
            vat={vat}
          />
          </Link>
      </Box>
    )
  }

}
